import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Lightbox } from 'ngx-lightbox';
import { SignPhoneNumberAdjustment } from 'src/app/model/sign/phone/sign-phone-number-adjustment';
import { SignPhoneNumberAdjustmentAsset } from 'src/app/model/sign/phone/sign-phone-number-adjustment-asset';
import { SignDetail } from 'src/app/model/sign/sign-detail';
import {
  ModalService,
  ObservableHandlerPair,
} from 'src/app/services/modal.service';
import { cloneDeep } from 'lodash';
import { SignPhoneNumberAdjustmentEditComponent } from '../../edit-association/phone/sign-phone-number-adjustment-edit.component';
import { SignPhoneNumberAdjustmentControllerRequestService } from 'src/app/services/api/sign-associations/implementations/sign-phone-number-adjustment-controller-request.service';
import { SubscriptionWithDestroyTriggerComponent } from 'src/app/subscription-helpers/subscription-with-destroy-trigger.component';
import { SignPhoneNumberAdjustmentOverview } from 'src/app/model/sign/phone/sign-phone-number-adjustment-overview';
import { of, Subject } from 'rxjs';
import { catchError, startWith, switchMap, takeUntil } from 'rxjs/operators';
import { INT_MAX_SIGNED_32_BIT_VALUE } from 'src/globals/globals';
import { ProperPageResult } from 'src/app/model/proper-page-result';

@Component({
  selector: 'sign-phone-number-adjustment-overview',
  templateUrl: './sign-phone-number-adjustment-overview.component.html',
  styleUrls: [
    './sign-phone-number-adjustment-overview.component.scss',
    '../sign-associations-overview-shared-style.scss',
  ],
})
export class SignPhoneNumberAdjustmentOverviewComponent
  extends SubscriptionWithDestroyTriggerComponent
  implements OnInit, OnDestroy
{
  @Input()
  sign: SignDetail;

  loading: boolean = false;
  signPhoneNumberAdjustments: SignPhoneNumberAdjustmentOverview[];

  private getSignPhoneNumberAdjustmentSubject: Subject<any>;

  constructor(
    private modalService: ModalService,
    private signPhoneNumberAdjustmentControllerRequestService: SignPhoneNumberAdjustmentControllerRequestService,
    private _lightbox: Lightbox
  ) {
    super();
  }

  ngOnInit(): void {
    this.getSignPhoneNumberAdjustmentSubject = new Subject<any>();

    this.addSubscription(
      this.getSignPhoneNumberAdjustmentSubject.pipe(
        startWith({}),
        switchMap(async () => {
          this.loading = true;
          return await this.signPhoneNumberAdjustmentControllerRequestService.getPaged(
            this.sign.id,
            null,
            'id',
            true,
            INT_MAX_SIGNED_32_BIT_VALUE,
            0,
            false
          );
        }),
        catchError(() => {
          return of({});
        }),
        takeUntil(this.destroyThisComponent)
      ),
      (res: ProperPageResult<SignPhoneNumberAdjustmentOverview>) => {
        this.signPhoneNumberAdjustments = res == null ? [] : res.items;
        this.loading = false;
      }
    );
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    if (this.getSignPhoneNumberAdjustmentSubject != null) {
      this.getSignPhoneNumberAdjustmentSubject.complete();
    }
  }

  addItem(): void {
    this.openEditModal();
  }

  editItem(signPhoneNumberAdjustment: SignPhoneNumberAdjustment): void {
    this.openEditModal(signPhoneNumberAdjustment);
  }

  private openEditModal(
    signPhoneNumberAdjustment?: SignPhoneNumberAdjustment
  ): void {
    const assocObject =
      signPhoneNumberAdjustment == null
        ? null
        : cloneDeep(signPhoneNumberAdjustment);
    this.modalService.openComponentInModal(
      SignPhoneNumberAdjustmentEditComponent,
      [
        { key: 'signId', value: this.sign.id },
        { key: 'assocObject', value: assocObject },
      ],
      new ObservableHandlerPair(
        (modalRef) => modalRef.componentInstance.saveSuccessEvent,
        (res: SignPhoneNumberAdjustment) => {
          if (res != null) {
            this.getSignPhoneNumberAdjustmentSubject.next();
          }
        }
      ),
      { width: '750px', maxHeight: '90vh' }
    );
  }

  deleteItem(signPhoneNumberAdjustment: SignPhoneNumberAdjustment): void {
    this.modalService.confirmDialog(
      'Telefoonnummer aanpassing verwijderen',
      'Bent u zeker dat u deze telefoonnummer aanpassing wilt verwijderen?',
      (b: boolean) => {
        if (b) {
          this.performDelete(signPhoneNumberAdjustment);
        }
      }
    );
  }

  private async performDelete(
    signPhoneNumberAdjustment: SignPhoneNumberAdjustment
  ): Promise<void> {
    this.loading = true;
    try {
      await this.signPhoneNumberAdjustmentControllerRequestService.archive(
        signPhoneNumberAdjustment.id
      );
      this.getSignPhoneNumberAdjustmentSubject.next();
    } catch (err) {
      this.loading = false;
      throw err;
    }
  }

  openLightbox(
    signPhoneNumberAdjustmentAssets: SignPhoneNumberAdjustmentAsset[]
  ) {
    let albums = [];
    for (let asset of signPhoneNumberAdjustmentAssets) {
      if (asset.archived) {
        continue;
      }
      const src = '/api/assets/' + asset.asset.uuid;
      const caption = 'Ontwerp';
      const thumb = '/api/assets/' + asset.asset.uuid;
      const album = {
        src: src,
        caption: caption,
        thumb: thumb,
      };

      albums.push(album);
    }
    this._lightbox.open(albums, 0);
  }
}
